import { pathToURL, ViewingService } from "../../net/Xhr";
import { initPlacement } from "./SvfPlacementUtils";
import { unpackHexString } from '../encoding/hex-strings';
import { LmvVector3 } from '../../wgs/scene/LmvVector3';
import { MeshFlags } from '../../wgs/scene/MeshFlags';
import { FragmentFlags } from './FragmentFlags';
import { LmvBox3 } from '../../wgs/scene/LmvBox3';
import { LmvMatrix4 } from '../../wgs/scene/LmvMatrix4';
import { allocateUintArray } from "../../wgs/scene/IntArray";
import { IdMapper } from "../encoding/IdMapper";
import { ProgressiveReadContext2 } from "./ProgressiveReadContext2";
import { isNodeJS, getGlobal } from "../../compat";
import { ResourceHashes } from "./ResourceHashes";
import { copySanitizedLoadContext } from "../../net/endpoints";
import { getElementCacheWorkerSingleton } from "../worker/elementcache/DtElementCacheWorkerSingleton";

/** @import { DtLoader } from "./DtLoader" */

let _b = new LmvBox3();
let _m = new LmvMatrix4(true);

function extractBox(src, srfOffset, fragOffset) {

  _b.min.x = src[srfOffset] + fragOffset.x;
  _b.min.y = src[srfOffset + 1] + fragOffset.y;
  _b.min.z = src[srfOffset + 2] + fragOffset.z;
  _b.max.x = src[srfOffset + 3] + fragOffset.x;
  _b.max.y = src[srfOffset + 4] + fragOffset.y;
  _b.max.z = src[srfOffset + 5] + fragOffset.z;

  return _b;
}
export class FragmentStorage {

  constructor(numFrags, numGeoms, numMaterials, doublePrecision) {
    this.length = numFrags;
    this.numLoaded = 0;
    this.boxes = doublePrecision ? new Float64Array(this.length * 6) : new Float32Array(this.length * 6);
    this.polygonCounts = new Uint32Array(this.length);
    this.transforms = doublePrecision ? new Float64Array(this.length * 12) : new Float32Array(this.length * 12);
    this.materials = numMaterials ? allocateUintArray(this.length, numMaterials) : new Array(this.length);
    this.geomDataIndexes = numGeoms ? allocateUintArray(this.length, numGeoms) : new Array(this.length);
    this.fragId2dbId = new Int32Array(this.length);
    this.dbId2fragId = {};

    // This is shared with the fragment list and stores MeshFlags.
    this.visibilityFlags = new Uint16Array(this.length);
    this.visibilityFlags.fill(MeshFlags.MESH_VISIBLE);

    // This is internal to the loader and used to keep track of fragment properties.
    this.fragmentFlags = new Uint8Array(this.length);
  }

  initEmptyFragments(fragId2dbId) {

    this.fragId2dbId = fragId2dbId;
    this.dbId2fragCount = new Map();
    this.hasFragMapping = true;

    for (let i = 0; i < this.length; i++) {

      this.visibilityFlags[i] |= MeshFlags.MESH_NOTLOADED;

      //Initialize bounding box to empty (max < min)
      let bo = i * 6;
      let boxes = this.boxes;
      boxes[bo + 3] = -1;
      boxes[bo + 4] = -1;
      boxes[bo + 5] = -1;
    }

    for (let i = 0; i < fragId2dbId.length; i++) {
      this.addDbIdReference(i, fragId2dbId[i]);
    }
  }

  getNextFragIdSlot(dbId) {

    let fragIds = this.dbId2fragId[dbId];
    let numUsedSlots = this.dbId2fragCount.get(dbId) || 0;

    if (fragIds === undefined) {
      console.warn("fragIds is undefined for dbid: ", dbId);
      return;
    }

    if (typeof fragIds === "number") {
      if (numUsedSlots >= 1) {
        console.log("unexpected number of fragments for element");
      } else {
        numUsedSlots++;
      }
      this.dbId2fragCount.set(dbId, numUsedSlots);
      return fragIds;
    } else {
      if (numUsedSlots >= fragIds.length) {
        console.log("unexpected number of fragments for element");
      } else {
        numUsedSlots++;
      }
      this.dbId2fragCount.set(dbId, numUsedSlots);
      return fragIds[numUsedSlots - 1];
    }
  }

  resetFragIdSlot(dbId) {

    if (typeof dbId === "number") {
      this.dbId2fragCount.set(dbId, 0);
    } else {
      for (let dbId of this.dbId2fragCount.keys()) {
        this.dbId2fragCount.set(dbId, 0);
      }
    }

  }

  addDbIdReference(fragId, dbId) {

    this.fragId2dbId[fragId] = dbId;

    let fid = this.dbId2fragId[dbId];
    if (fid === undefined) {
      this.dbId2fragId[dbId] = fragId;
    } else {
      if (typeof fid === "number") {
        this.dbId2fragId[dbId] = [fid, fragId];
      } else {
        fid.push(fragId);
      }
    }
  }

  setTransform(fragId, m) {
    let e = m.elements;
    let dst = this.transforms;
    let off = fragId * 12;
    dst[off + 0] = e[0];
    dst[off + 1] = e[1];
    dst[off + 2] = e[2];
    dst[off + 3] = e[4];
    dst[off + 4] = e[5];
    dst[off + 5] = e[6];
    dst[off + 6] = e[8];
    dst[off + 7] = e[9];
    dst[off + 8] = e[10];
    dst[off + 9] = e[12];
    dst[off + 10] = e[13];
    dst[off + 11] = e[14];
  }

  setBoundingBox(fragId, b) {

    let dst = this.boxes;
    let dstOffset = fragId * 6;

    dst[dstOffset] = b.min.x;
    dst[dstOffset + 1] = b.min.y;
    dst[dstOffset + 2] = b.min.z;
    dst[dstOffset + 3] = b.max.x;
    dst[dstOffset + 4] = b.max.y;
    dst[dstOffset + 5] = b.max.z;
  }

  setFragmentLoaded(fragId, value) {
    if (value) {
      this.fragmentFlags[fragId] = this.fragmentFlags[fragId] | FragmentFlags.IS_LOADED;
    } else {
      this.fragmentFlags[fragId] = this.fragmentFlags[fragId] & ~FragmentFlags.IS_LOADED;
    }
  }

  isFragmentLoaded(fragId) {
    return !!(this.fragmentFlags[fragId] & FragmentFlags.IS_LOADED);
  }

  setMeshLoaded(fragId, value) {
    //Note the flag bit represents the opposite of the function signature -- not loaded vs loaded.
    if (value) {
      this.visibilityFlags[fragId] = this.visibilityFlags[fragId] & ~MeshFlags.MESH_NOTLOADED;
    } else {
      this.visibilityFlags[fragId] = this.visibilityFlags[fragId] | MeshFlags.MESH_NOTLOADED;
    }
  }

  isMeshLoaded(fragId) {
    return !(this.visibilityFlags[fragId] & MeshFlags.MESH_NOTLOADED);
  }

  isRoom(fragId) {
    return (this.fragmentFlags[fragId] & FragmentFlags.IS_ROOM) !== 0;
  }
}


export function DtPackage(loadContext, parentLoader) {

  this.loadContext = loadContext;

  /** @type {DtLoader} */
  this.parentLoader = parentLoader;
  this.modelId = loadContext.modelId || "unknown";

  this.materials = null; //The materials json as it came from the SVF

  /** @type {FragmentStorage} */
  this.fragments = null; //will be wrapped in a FragmentList

  this.bbox = null; //Overall scene bounds

  this.globalOffset = { x: 0, y: 0, z: 0 };
  this.placementWithOffset = null;
  this.placementIsTranslation = false;

  this.fragmentsLoaded = false;
  this.fragmentsInProgress = false;
  this.queuedFragmentRequests = [];
  this._numFragsInProgress = 0;

  this.metadataInProgress = false;

  this.aborted = false;

  const useCache = !isNodeJS() && (getGlobal().ENABLE_MODEL_CACHE ?? false);
  // TODO: Remove once we don't need the feature flags anymore.
  const useFragmentCache = useCache && this.parentLoader.viewer3DImpl.api.getFeatureFlags()?.fragmentCache;
  const useItCache = useCache && this.parentLoader.viewer3DImpl.api.getFeatureFlags()?.instanceTreeCache;
  // TODO: This should basically just be '= useCache' once the flags are removed.
  this.useElementCache = useFragmentCache || useItCache;
}

//Set up the fragments, materials, and meshes lists
//which get filled progressively as we receive their data
DtPackage.prototype.initEmptyLists = function (stats) {

  let svf = this;
  let loadContext = this.loadContext;

  // Stats are given when loading the fragment list first, while these properties are only set when loading the
  // instance tree first.
  let autoDbIds = loadContext.autoDbIds;
  let fragId2dbId = loadContext.fragId2dbId;
  let numFrags = fragId2dbId ? fragId2dbId.length : 0;

  if (!stats && !fragId2dbId) {
    console.warn("Expect at least one of stats or fragId2DbId to be specified to initialize fragment list");
  }

  if (stats) {
    // Case where we load the fragment list before the instance tree.
    // We pre-allocate geometry and material lists based on metadata, because we load all fragments.
    svf.fragments = new FragmentStorage(stats.num_fragments, stats.num_geoms, stats.num_materials, loadContext.fragmentTransformsDouble);
  } else {
    // Case where we load the instance tree first.
    // We keep the geometry and material lists dynamic, because we're likely only loading a subset of all fragments.
    svf.fragments = new FragmentStorage(numFrags, 0, 0, loadContext.fragmentTransformsDouble);
    svf.fragments.initEmptyFragments(fragId2dbId);
  }

  svf.geomMetadata = new ResourceHashes();

  svf.materialHashes = new ResourceHashes();

  svf.autoDbIds = new IdMapper(autoDbIds);

  //Shell object to make it compatible with SVF.
  //Not sure which parts of this are really needed,
  //SceneUnit is one.
  svf.materials = {
    "name": "LMVTK Simple Materials",
    "version": "1.0",
    "scene": {
      "SceneUnit": 8215,
      "YIsUp": 0
    },
    materials: {}
  };

};

DtPackage.loadAsyncResource = function (loadContext, resourcePath, responseType, callback, onErrorCB) {

  //Launch an XHR to load the data from external file

  resourcePath = pathToURL(resourcePath, loadContext.basePath);

  function onError(...args) {
    loadContext.onFailureCallback?.(...args);
    onErrorCB(...args);
  }

  ViewingService.getItem(loadContext, resourcePath, callback, onError,
  { responseType: responseType || "arraybuffer" }
  );
};

function getAsyncProgressiveHandlers(loadContext, svf, ctx, postData, resolve, reject) {
  const onDone = function () {
    if (svf.aborted) {
      return;
    }

    ctx.onEnd();

    svf.onFragmentsLoaded({ keys: postData.rowIds, numExpectedFragments: loadContext.numExpectedFragments });

    resolve();
  };

  const onError = function (...args) {
    if (svf.aborted) {
      return;
    }

    // Some errors are not network errors, but exceptions in the worker.
    // No need to raise them via the network error handler.
    if (!(args[0] instanceof Array && args[0][0] === 'fragment worker exception')) {
      loadContext.onFailureCallback?.(...args);
    }
    reject(...args);
  };

  const onData = function (chunk) {
    if (svf.aborted) {
      return true;
    }

    //Read as many fragments as we can at this time
    ctx.onData(chunk, false);
  };

  return { onDone, onError, onData };
}

/**
 * Run an operation in the property worker.
 * @param {Object} message The message object. Model and twin urns will be injected into all messages.
 *  Note that this method doesn't make any assumptions on what the worker operation does, i.e. it doesn't inject any
 *  other information into the message. Everything that is required needs to be provided by the caller
 *  (e.g. endpoint configuration properties if the worker operation needs those to make network requests).
 * @param {Function} [onDone] An optional success handler method. Will be called with 'response.result' if a worker
 *  response message does not include a 'progress' or 'error' property. No other callbacks will be invoked after this
 *  (i.e. the worker operation is considered done and should not send any further responses).
 * @param {Function} [onError] An optional error handler method. Will be called if a worker response includes an
 *  'error' property. No other callbacks will be invoked after this (i.e. the worker operation is considered done and
 *  should not send any further responses).
 * @param {Function} [onProgress] An optional progress handler method. Will be called if a worker response includes a
 *  'progress' property. This may be called multiple times.
 */
DtPackage.prototype._runPropertyWorkerOperation = function (message, onDone, onError, onProgress) {
  const model = this.parentLoader.model;
  const facility = model.getParentFacility();

  // Inject model and twin urns into the message.
  // The model id is used in almost all operations. The twin id may be used to determine cache usage.
  message.modelId = model.urn();
  message.twinId = facility.urn();

  const worker = facility.getWorker(model.id);

  // Only register response callbacks if we actually expect a response from the worker.
  // If not, the operation in the worker needs to make sure that no response is sent automatically from doWrapper.
  // Note that we don't need to unregister the callbacks explicitly.
  // This will be done automatically when either the success (done) or error callback get invoked.
  if (onDone || onError || onProgress) {
    const cbId = worker.registerWorkerCallback(onDone, onError, onProgress);
    message.cbId = cbId;
  }

  // Send the message to the worker.
  worker.doOperation(message);
};

DtPackage.prototype.loadFragmentListFromWorker = async function (loadContext, resourcePath, ctx, postData) {

  return new Promise((resolve, reject) => {

    let svf = this;

    resourcePath = pathToURL(resourcePath, loadContext.basePath);

    const { onDone, onError, onData } = getAsyncProgressiveHandlers(loadContext, svf, ctx, postData, resolve, reject);

    /**
     * What we receive from the worker differs per data type and scenario.
     *
     * For the dbId <-> element id mapping, we should only receive data (non-empty dbIds2ElmIds array) if the
     * fragment list is loaded prior to the instance tree. In that case, the mapping will be sent in chunks,
     * i.e. across multiple message, similar to the actual fragment data.
     * If the instance tree gets loaded prior to any fragment list requests, the mapping should already be complete
     * and no new id pairs should be received from the worker. If the worker sends some pairs anyways, that
     * would indicate a mismatch between the instance tree and fragment list element ids and should be
     * considered an error. We don't currently handle this case, though.
     *
     * For the geomId <-> geomHash map (same for materials), there are two different scenarios as well:
     * If data is served from the cache, the worker will return the full ResourceHashes instance (for all
     * fragments) in the first message, regardless of whether we request a subset or all fragments. Subsequent
     * fragment list requests will not contain this data again.
     * If data is served via the network, we receive an array with the id / hash pairs for the fragments that we
     * requested, and need to add them to our local ResourceHashes instance. The data will be sent in chunks
     * (i.e. multiple messages), similar to the actual fragment data.
     */

    const onData2 = (message) => {
      const {
        chunk,
        dbIds2ElmIds,
        geomResourceHashes,
        matResourceHashes,
        geomHashIdList,
        matHashIdList
      } = message;

      for (let i = 0; i < dbIds2ElmIds.length; i += 2) {
        // Add dbId / hash pairs to the IdMapper.
        svf.autoDbIds.addIdAndHash(dbIds2ElmIds[i], dbIds2ElmIds[i + 1]);
      }

      if (geomResourceHashes) {
        // We got the complete ResourceHashes instance (for all fragments) from the cache. Just use it.
        svf.geomMetadata = new ResourceHashes(geomResourceHashes);
      } else if (geomHashIdList) {
        // Data is served from the network. We get a list of id / hash pairs that we need to add to our
        // ResourceHashes instance.
        for (let i = 0; i < geomHashIdList.length; i += 2) {
          svf.geomMetadata.addIdAndHash(geomHashIdList[i], geomHashIdList[i + 1]);
        }
      }

      if (matResourceHashes) {
        // We got the complete ResourceHashes instance (for all fragments) from the cache. Just use it.
        svf.materialHashes = new ResourceHashes(matResourceHashes);
      } else if (matHashIdList) {
        // Data is served from the network. We get a list of id / hash pairs that we need to add to our
        // ResourceHashes instance.
        for (let i = 0; i < matHashIdList.length; i += 2) {
          svf.materialHashes.addIdAndHash(matHashIdList[i], matHashIdList[i + 1]);
        }
      }

      // Forward fragment data to the progressive reader.
      return onData(chunk);
    };

    svf.fragmentsCtx = ctx;

    postData = postData || {};

    let metadata;
    if (svf.metadata) {
      metadata = {
        fragmentTransformsOffset: svf.metadata.fragmentTransformsOffset,
        placementWithOffset: svf.placementWithOffset
      };
    }

    // TODO: Probably no need to provide the dbId map anymore. Also remove / update the onData handler above.
    // Update worker code to send updates as required and handle this in the main thread.

    // Send the geomHash -> geomId map (same for materials) to the worker if they exist.
    let geomIdMap = svf.geomMetadata?.hashToIndex;
    let matIdMap = svf.materialHashes?.hashToIndex;

    // The node.js code path uses fake workers and runs everything in the main thread, so sending the maps to the
    // worker won't copy them. We need to copy them manually here, because modifying them in the worker code has
    // undesired side effects.
    if (isNodeJS()) {
      if (geomIdMap) {
        geomIdMap = new Map(geomIdMap);
      }
      if (matIdMap) {
        matIdMap = new Map(matIdMap);
      }
    }

    const message = {
      operation: 'DT_LOAD_FRAGMENT_LIST',
      resourcePath,
      dbPath: svf.parentLoader.loadContext.dbPath,
      metadata,
      postData,
      matIdMap,
      geomIdMap
    };

    // Inject endpoint configuration required for network requests.
    copySanitizedLoadContext(message, this.loadContext);
    svf._runPropertyWorkerOperation(message, onDone, onError, onData2);
  });
};

DtPackage.prototype.sendMetadataToWorker = async function () {
  const metadata = {
    fragmentTransformsOffset: this.metadata.fragmentTransformsOffset,
    placementWithOffset: this.placementWithOffset,
    placementIsTranslation: this.placementIsTranslation
  };

  const message = {
    operation: 'DT_SET_FRAGMENT_METADATA',
    metadata
  };

  this._runPropertyWorkerOperation(message);
};

DtPackage.prototype.loadMetadata = async function (path, skipFragmentInit) {

  let svf = this;
  let loadContext = this.loadContext;
  svf.metadataInProgress = true;

  return new Promise((resolve, reject) => {

    DtPackage.loadAsyncResource(loadContext, path, "json", function (data) {

      //For OTG, there is a single JSON for metadata and manifest,
      //and it's the root
      svf.metadata = data;
      svf.manifest = svf.metadata.manifest;

      // Set numGeoms. Note that this must happen before creating the GeometryList.
      svf.numGeoms = svf.metadata.stats.num_geoms;

      svf.processMetadata();

      svf.sendMetadataToWorker();

      //Was fragment list already initialized by the object tree loading step?
      //If not, do it now.
      if (!skipFragmentInit) {
        svf.initEmptyLists(svf.metadata.stats);
      }

      svf.metadataInProgress = false;

      resolve(svf.metadata);
    }, reject);
  });
};

DtPackage.prototype.prepareModelHistory = async function () {
  if (!this.useElementCache) {
    return;
  }

  // Technically, we could just hide this from the main thread and silently load the model's history in the fragment
  // list worker. It's only required to check if the cache is still up-to-date. But then we would only get to kick
  // off the request once the fragment list is requested, so loading would get delayed. It's better to let the main
  // thread instruct the worker to pre-load the history a bit earlier in the load process via this function.
  const resourcePath = pathToURL('history', this.loadContext.basePath);

  const modelId = this.loadContext.modelId;
  const msg = {
    operation: "DT_LOAD_MODEL_HISTORY",
    modelId,
    resourcePath
  };

  // Inject endpoint configuration required for network requests.
  copySanitizedLoadContext(msg, this.loadContext);
  const sessionId = this.parentLoader.viewer3DImpl.api._sessionId;
  const elementCacheWorker = getElementCacheWorkerSingleton(sessionId);
  elementCacheWorker.doOperation(msg);
};

DtPackage.prototype.computeMissingFragments = function (dbIdFilter) {

  let fragStorage = this.fragments;

  let dbIdsToLoad;
  let fragmentsToActivate;
  let numFragments = 0;

  //If it makes sense, load a subset of the fragment list
  if (fragStorage?.hasFragMapping && dbIdFilter) {
    //Case where the instance tree was loaded in full first, and then the fragment list
    //was loaded potentially in parts

    dbIdsToLoad = new Set();
    fragmentsToActivate = [];
    for (let dbId of dbIdFilter.values()) {
      let needLoad = false;
      let numFrags = 0;
      this.instanceTree.enumNodeFragments(dbId, (fragId) => {
        ++numFrags;
        if (!fragStorage.isFragmentLoaded(fragId)) {
          needLoad = true;
        } else {
          fragmentsToActivate.push(fragId);
        }
      }, false);

      if (needLoad) {
        numFragments += numFrags;
        dbIdsToLoad.add(dbId);
      }
    }
  } else if (this.fragmentsLoaded && fragStorage) {
    // Case where the full fragment list was loaded up front.
    // The initial full fragment list request (triggered by loader.beginLoad) will not get into this path
    // (because the asynchronously requested metadata didn't arrive yet).

    dbIdsToLoad = new Set();
    fragmentsToActivate = [];
    let fragId2dbId = this.fragments.fragId2dbId;
    for (let fragId = 0; fragId < fragId2dbId.length; fragId++) {
      let dbId = fragId2dbId[fragId];
      if (dbId && !fragStorage.isFragmentLoaded(fragId)) {
        numFragments++;
        dbIdsToLoad.add(dbId);
      } else {
        fragmentsToActivate.push(fragId);
      }
    }
  }

  if (numFragments > 0 && numFragments === this.fragments?.length) {
    // We need to load all fragments. Avoid using a list of dbIds by returning undefined.
    dbIdsToLoad = undefined;
  }

  return { dbIdsToLoad, fragmentsToActivate, numFragments };
};

let _zero = { x: 0, y: 0, z: 0 };

DtPackage.prototype.receiveOneFragmentFromWorker = function (ctx, idx, baseOffset, dbIdsToLoad, dbIdFilter) {

  //Fragments have ot wait for the metadata (placement transform)
  //before they can be fully processed
  if (!this.metadata)
  return false;

  let idata = ctx.idata();
  let fdata = ctx.fdata();
  let offset = baseOffset / 4;

  let frags = this.fragments;

  let fragId;
  const dbId = idata[0];

  //Check if this is a "surplus" fragment that we requested because
  //we expanded the request list to minimize the POST data payload,
  //and skip processing if so.
  if (dbIdsToLoad && !dbIdsToLoad.has(dbId)) {
    return true;
  }

  if (frags.hasFragMapping) {
    fragId = frags.getNextFragIdSlot(dbId);
  } else {
    //The index is 1-based (due to the record at index 0 being the file header
    //while the runtime FragmentList is a classic 0-based array, so we subtract 1 here.
    fragId = idx - 1;
    frags.addDbIdReference(fragId, dbId);
  }

  const meshid = idata[1];
  frags.geomDataIndexes[fragId] = meshid;

  frags.materials[fragId] = idata[2];

  let flags = idata[offset + 3];

  //Decode mesh flags (currently, we only support bit value 1 to mark hidden-by-default meshes)
  //If a filter is specified, set the "unloaded" flag to prevent the mesh geometry from being loaded up front
  let skipMeshBit = 0;
  if (dbIdFilter) {
    if (!dbIdFilter.has(dbId)) {
      skipMeshBit = MeshFlags.MESH_NOTLOADED;
    }
  }

  //Is the fragment marked as hidden geometry? Then mark it as hidden but do not skip loading of the geometry
  const hiddenBit = flags & 1 ? MeshFlags.MESH_HIDE : 0;

  frags.visibilityFlags[fragId] |= hiddenBit | skipMeshBit;
  if (!skipMeshBit) {
    // Unset the MESH_NOTLOADED flag that might have been set in initEmptyFragments.
    frags.visibilityFlags[fragId] &= ~MeshFlags.MESH_NOTLOADED;
  }

  // Read the fragment transform
  _m.elements.set(fdata.subarray(offset + 4, offset + 20));
  frags.setTransform(fragId, _m);

  //Read off bounding box
  let b = extractBox(fdata, offset + 20, _zero /*lo*/);
  frags.setBoundingBox(fragId, b);

  frags.polygonCounts[fragId] = idata[offset + 26] & 0xffffff;

  const consolidationBit = frags.polygonCounts[fragId] < this.loadContext.consolidationPolyCountLimit ?
  FragmentFlags.IS_CONSOLIDATED ?? 0 : 0;

  // We expect hidden-by-default meshes to be rooms.
  const isRoomBit = hiddenBit ? FragmentFlags.IS_ROOM : 0;

  //Transparent bit -- this hack marks meshes with the hide flag to also be transparent.
  //This helps sort room meshes (which come with the hidden flag set) into the transparent objects bucket
  //of the spatial index. That will make sure rooms draw last, on top of opaque objects and will have slightly
  //more correct rollover highlighting.
  let transparentBit = hiddenBit ? FragmentFlags.IS_TRANSPARENT : 0;

  // Read the actual transparency information and update the transparent bit.
  let transparent = idata[offset + 26] >> 24 & 0x1 ? FragmentFlags.IS_TRANSPARENT : 0;
  transparentBit |= transparent;

  // Add transparency information to the fragment flags. The BVH Builder will take it from there.
  frags.fragmentFlags[fragId] = FragmentFlags.IS_LOADED | transparentBit | isRoomBit | consolidationBit;

  frags.numLoaded++;

  this.parentLoader.notifyFragmentDone(fragId);

  return true;

};

DtPackage.prototype._addWorkInProgress = function (numFragments) {
  this._numFragsInProgress += numFragments;
  this.parentLoader.viewer3DImpl.geomCache().addWorkInProgress(numFragments);

  // Unset these values in case they have been set by a previous call to model.boxesLoadingDone
  // (see _updateProgress below).
  this.fragments.dontComputeBoxes = false;
  this.fragments.boxesLoaded = false;
};

DtPackage.prototype._abortWorkInProgress = function () {
  if (this._numFragsInProgress) {
    this.parentLoader.viewer3DImpl.geomCache().updateProgress(this._numFragsInProgress);
    this._numFragsInProgress = 0;
    this.parentLoader.viewer3DImpl.geomCache().unregisterLoader(this.modelId, this.loadContext);
  }
};

DtPackage.prototype._updateProgress = function (numFragments) {
  this._numFragsInProgress -= numFragments;
  if (this._numFragsInProgress === 0) {
    this.parentLoader.viewer3DImpl.geomCache().unregisterLoader(this.modelId, this.loadContext);

    const model = this.parentLoader.model;
    // This fires the MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT for the model.
    // We could call this even earlier, after all (currently requested) fragments are loaded,
    // but that would make it harder to determine when it actually needs to be called.
    // Doing it here covers all scenarios.
    // Note that it sets frags.dontComputeBoxes and frags.boxesLoaded, which are used by LMV.
    // So we need to unset them again before loading more fragments (see _addWorkInProgress).
    model.boxesLoadingDone();

    // This fires the GEOMETRY_LOADED_EVENT for the model
    // TODO-LMV: Firing this event multiple times may cause issues, e.g. recomputing a ray cast BVH in BimWalk unnecessarily. Ideally, we don't run out of work before all geoms are loaded.
    this.parentLoader.viewer3DImpl.onLoadComplete(model);
  }

  // This may fire the DT_LOADER_IDLE_EVENT if all pending requests (across models) are done.
  this.parentLoader.viewer3DImpl.geomCache().updateProgress(numFragments);
};

DtPackage.prototype._activateLoadedFragments = function (fragmentsToActivate) {
  if (fragmentsToActivate?.length) {
    // Disable progress tracking for the synchronous loop below. Calling notifyFragmentDone calls
    // tryToActivateFragment, which is basically a no-op if all resources (geom, mat) are already loaded.
    // It still updates the load progress in these cases if not disabled here, which would mess things up.
    // Only fragments that actually load resources via the geom cache are supposed to be tracked.
    this.parentLoader.trackFragmentProgress = false;

    const fragStorage = this.fragments;
    let fragId;
    let numFragsToLoad = 0;
    for (let i = 0; i < fragmentsToActivate.length; ++i) {
      fragId = fragmentsToActivate[i];
      // TODO: This is very confusing. Also see loader.tryToActivateFragments (skipLoad). Right now, it seems
      // like this flag indicates the desire to load the geometry. If it's not set, we skip loading. This should
      // be changed to actually represent the loaded state (and hence shouldn't be set here, but only after
      // loading / unloading the geometry for a fragment).
      fragStorage.setMeshLoaded(fragId, true);

      // If the fragment's geometry and material are already loaded, `notifyFragmentDone` returns true.
      // We count the number of fragments that we actually need to load data for and only communicate those to
      // the geom cache for progress tracking.
      if (!this.parentLoader.notifyFragmentDone(fragId)) {
        numFragsToLoad++;
      };
    }

    if (numFragsToLoad) {
      // Keep track of the fragments that we actually load resources (geoms, materials) for
      // (to update the progress bar).
      this._addWorkInProgress(numFragsToLoad);
    }

    this.parentLoader.trackFragmentProgress = true;
  }
};

DtPackage.prototype.loadFragmentList = async function (ids, resolveDeferred, rejectDeferred) {

  let svf = this;
  let loadContext = this.loadContext;

  if (!svf.metadata && !svf.metadataInProgress) {
    console.warn("Metadata needs to be loaded before or during a fragment list request");
  }

  if (svf.fragmentsInProgress) {
    //Avoid re-entry into loadFragmentList.
    //This code path will remember resolve/reject and later pass them in
    //as resolveDeferred and rejectDeferred when the request is actually processed
    return new Promise((resolve, reject) => {
      this.queuedFragmentRequests.push({ ids, resolve, reject });
    });
  }

  svf.fragmentsInProgress = true;

  let dbIdFilter;
  if (Array.isArray(ids)) {
    dbIdFilter = new Set(ids);
  } else if (ids && typeof ids === "object") {
    dbIdFilter = new Set(Object.keys(ids));
  }

  //Flag if _all_ fragments are loaded
  if (!dbIdFilter) {
    svf.fragmentsLoaded = true;
  }

  //Find out which fragments are missing locally and need to be requested
  let { dbIdsToLoad, fragmentsToActivate, numFragments } = this.computeMissingFragments(dbIdFilter);

  //No missing fragments to load, skip the request.
  if (dbIdsToLoad && dbIdsToLoad.size === 0) {
    svf._activateLoadedFragments(fragmentsToActivate);
    svf.onFragmentsLoaded({ keys: [] });
    resolveDeferred?.();
    return;
  } else if (!dbIdsToLoad) {
    // We need to load all fragments. If the metadata is already known, we can set the number of expected fragments.
    if (svf.metadata) {
      numFragments = svf.fragments.length;
    }
  }

  //We are loading too many fragments, request the whole fragment list instead, to avoid a large post payload
  //and large key list query on the server.
  let isTooMany = false;
  if (svf.fragments && dbIdsToLoad && dbIdsToLoad.size > 0.25 * svf.fragments.length) {
    for (let dbId of dbIdsToLoad) {
      svf.fragments.resetFragIdSlot(dbId);
    }
    isTooMany = true;
  }

  // TODO: Move the 'isTooMany' decision to the worker. No point in defining a subset here, then requesting
  // everything from the cache and filter it in the main thread, if we could also just filter on the cache level
  // already.
  let rowIds;
  if (!isTooMany && dbIdsToLoad) {
    rowIds = new Set();
    for (let dbId of dbIdsToLoad) {
      // Note that we don't include key flags (i.e. physical vs logical) in the requested ids.
      // The cache worker will add the physical flag when requesting fragment data from the server.
      rowIds.add(this.autoDbIds.getElementId(dbId));
      svf.fragments.resetFragIdSlot(dbId);
    }
  }

  //If allowed (no recent changes), randomly direct the request to a database replica, rather than
  //the primary (random = based on the last byte if the model GUID).
  //let lastByte = loadContext.modelId.charCodeAt(loadContext.modelId.length - 1);
  let usePrimary = loadContext.mustUsePrimary; // || lastByte % 3 === 0; //assumes one primary and two replicas, for a total of 3.

  let postData = {
    modelId: loadContext.modelId,
    readPreference: usePrimary ? "primary" : "secondaryPreferred",
    keys: rowIds
  };

  //console.log("load fragment list", rowIds?.length);

  try {
    let t0 = performance.now();

    // Tell the geomCache how many fragments we're going to process (to update the progress bar).
    // If we're loading all fragments and metadata isn't there yet, numFragments will be 0.
    // In this case, we defer this call to after loading the metadata. Fragments won't be processed
    // until then anyway.
    if (numFragments > 0) {
      this._addWorkInProgress(numFragments);
      this.loadContext.numExpectedFragments = numFragments;
    } else {
      this.delayedSomeFragments = true;
    }

    let ctx = new ProgressiveReadContext2((ctx, idx) => {
      return this.receiveOneFragmentFromWorker.call(svf, ctx, idx, 0, dbIdsToLoad, dbIdFilter);
    }, 108);

    // Load fragment list from the worker
    const loadPromise = this.loadFragmentListFromWorker(loadContext, "fragments", ctx, postData);

    // Activate fragments that have already been loaded
    svf._activateLoadedFragments(fragmentsToActivate);

    await loadPromise;

    let t1 = performance.now();
    svf.parentLoader.logger.log(`frag load time (${this.modelId}):`, t1 - t0);
    resolveDeferred?.();
  } catch (e) {
    rejectDeferred?.(e);
  }
};

function closeEnough(x, y) {
  return Math.abs(x - y) < 1e-10;
}
function isTranslationOnly(m) {
  const e = m.elements;

  return closeEnough(e[0], 1) && closeEnough(e[1], 0) && closeEnough(e[2], 0) && closeEnough(e[3], 0) &&
  closeEnough(e[4], 0) && closeEnough(e[5], 1) && closeEnough(e[6], 0) && closeEnough(e[7], 0) &&
  closeEnough(e[8], 0) && closeEnough(e[9], 0) && closeEnough(e[10], 1) && closeEnough(e[11], 0) &&
  closeEnough(e[15], 1);
}

DtPackage.prototype.processMetadata = function () {

  let svf = this;

  let metadata = svf.metadata;

  initPlacement(svf, this.loadContext);
  let pt = svf.placementWithOffset;
  svf.placementIsTranslation = pt && isTranslationOnly(pt);

  if (metadata.cameras) {
    svf.cameras = metadata.cameras;

    if (!pt) {
      pt = new LmvMatrix4(true);
    }

    for (let i = 0; i < svf.cameras.length; i++) {
      let cam = svf.cameras[i];
      cam.position = new LmvVector3(cam.position.x, cam.position.y, cam.position.z);
      cam.position.applyMatrix4(pt);
      cam.target = new LmvVector3(cam.target.x, cam.target.y, cam.target.z);
      cam.target.applyMatrix4(pt);

      cam.up = new LmvVector3(cam.up.x, cam.up.y, cam.up.z);
      cam.up.transformDirection(pt);
    }
  }

  if (svf.metadata.aec) {
    const levelsMetadataMap = new Map();
    const aecMetadata = svf.metadata.aec.levels;
    if (aecMetadata) {
      for (const l of aecMetadata) {
        levelsMetadataMap.set(l.name, l);
      }
    }
    svf.levelsAecMetadataMap = levelsMetadataMap;
  }

};

DtPackage.prototype.beginLoad = function (otgPath) {

  if (this.metadata) {
    this.onMetadataLoaded();
  } else if (!this.metadataInProgress) {
    this.loadMetadata(otgPath, false).then(() => {
      this.onMetadataLoaded();
    });
  }

  this.loadFragmentList();
};

const prefixes = {
  "materials": "m",
  "geometry": "g",
  "textures": "t"
};

DtPackage.prototype.makeSharedResourcePath = function (cdnUrl, whichType, hash) {

  if (hash.length === 10)
  hash = unpackHexString(hash);

  return cdnUrl + "/" + prefixes[whichType] + "/" + this.modelId + "/" + hash;
};

DtPackage.prototype.getMaterialHash = function (materialIndex) {
  return this.materialHashes.hashes[materialIndex];
};

DtPackage.prototype.getGeometryHash = function (geomIndex) {
  return this.geomMetadata.hashes[geomIndex];
};

DtPackage.prototype.onMetadataLoaded = function () {

  this.parentLoader.notifyModelRootDone();

  if (this.delayedSomeFragments) {
    // We have requested all fragments but delayed processing until now.
    // Tell the resource cache how many we're going to process.
    this._addWorkInProgress(this.fragments.length);
    this.loadContext.numExpectedFragments = this.fragments.length;

    this.delayedSomeFragments = false;
  }

  if (this.delayedAllFragments) {
    //In case the fragment list already arrived, we need to finish processing it
    //once the metadata is here too
    this.delayedAllFragments = false;

    this.onFragmentsLoaded({ numExpectedFragments: this.loadContext.numExpectedFragments });
  }
};

DtPackage.prototype.onFragmentsLoaded = function (data) {

  //In rare cases the fragment list can appear before the metadata json is loaded
  //so we have to delay the final fragment list done notification until the metadata arrives
  if (this.metadataInProgress) {
    this.delayedAllFragments = true;
    return;
  }

  if (this.fragmentsCtx) {
    const numFrags = this.fragmentsCtx.flush();
    if (data.numExpectedFragments && numFrags < data.numExpectedFragments) {
      // In some cases, the metadata is wrong and we don't load as many fragments as we originally thought.
      // We have to inform the resource cache that the remaining fragments will never arrive, i.e. just mark them
      // as done.
      this._updateProgress(data.numExpectedFragments - numFrags);
    }
    this.fragmentsCtx = null;
  }

  this.loadContext.numExpectedFragments = undefined;

  if (!this.fragmentsInProgress) {
    console.warn("expected fragmentsInProgress flag");
  }
  this.fragmentsInProgress = false;

  this.parentLoader.notifyAllFragmentsDone(data);

  if (this.queuedFragmentRequests.length) {
    let nextFrags = this.queuedFragmentRequests.shift();
    this.loadFragmentList(nextFrags.ids, nextFrags.resolve, nextFrags.reject);
  }
};

DtPackage.prototype.abort = function () {
  this._abortWorkInProgress();
  this.aborted = true;
};

DtPackage.prototype.isPlacementInitialized = function () {
  return this.placementWithOffset !== null;
};